import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type ExpiredLotRow = {
  id: string;
  productId: string;
  batchCode?: string | null;
  expiryDate?: string | null;
  remainingQtyLbs: number;
  product: {
    id: string;
    sku: string;
    name: string;
    department?: { id: string; name: string; slug: string } | null;
  };
};

function ExpiredProductsPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const [expiredLots, setExpiredLots] = useState<ExpiredLotRow[]>([]);
  const [expiredLoading, setExpiredLoading] = useState(false);
  const [expiredError, setExpiredError] = useState<string | null>(null);

  const [expiredReasonByLotId, setExpiredReasonByLotId] = useState<Record<string, string>>({});
  const [expiredSavingByLotId, setExpiredSavingByLotId] = useState<Record<string, boolean>>({});
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [, setWorkspaceDepartments] = useState<
    Array<{ id: string; slug: string; name: string }>
  >([]);

  const isAllScope = React.useMemo(() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  }, [workspaceScope]);
  const scopedDepartmentSlug = React.useMemo(() => {
    if (isAllScope) return '';
    return String(workspaceScope || '').trim().toLowerCase();
  }, [isAllScope, workspaceScope]);
  const switchWorkspaceScope = React.useCallback((nextDepartmentSlug: string) => {
    const slug = String(nextDepartmentSlug || '').trim().toLowerCase();
    if (!slug || typeof window === 'undefined') return;
    window.localStorage.setItem('workspace_department_scope', slug);
    window.dispatchEvent(
      new CustomEvent('workspace-department-scope:change', {
        detail: { scope: slug },
      }),
    );
  }, []);

  const loadExpiredLots = React.useCallback(async () => {
    setExpiredLoading(true);
    setExpiredError(null);
    try {
      const res = await authorizedFetch(
        (() => {
          const url = new URL(`${API_BASE}/inventory/expired-lots`);
          url.searchParams.set('page', '1');
          url.searchParams.set('pageSize', '50');
          if (scopedDepartmentSlug) {
            url.searchParams.set('departmentSlug', scopedDepartmentSlug);
          }
          return url.toString();
        })(),
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message =
          (body as { message?: string }).message || 'Unable to load expired lots.';
        throw new Error(message);
      }
      const data = (await res.json()) as { items: ExpiredLotRow[] };
      setExpiredLots(data.items ?? []);
      setExpiredReasonByLotId((prev) => {
        const next: Record<string, string> = { ...prev };
        for (const l of data.items ?? []) {
          if (!next[l.id]) next[l.id] = 'EXPIRED';
        }
        return next;
      });
    } catch (err) {
      setExpiredError(err instanceof Error ? err.message : 'Unable to load expired lots.');
    } finally {
      setExpiredLoading(false);
    }
  }, [authorizedFetch, scopedDepartmentSlug]);

  useEffect(() => {
    void loadExpiredLots();
  }, [loadExpiredLots]);

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/departments/workspace`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ id?: string; slug?: string; name?: string }>;
        if (!cancelled) {
          setWorkspaceDepartments(
            (Array.isArray(rows) ? rows : [])
              .map((row) => ({
                id: String(row.id || '').trim(),
                slug: String(row.slug || '').trim().toLowerCase(),
                name: String(row.name || '').trim(),
              }))
              .filter((row) => Boolean(row.id && row.slug)),
          );
        }
      } catch {
        if (!cancelled) setWorkspaceDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  const recordExpiredLotLoss = React.useCallback(
    async (lot: ExpiredLotRow) => {
      const lotId = lot.id;
      const reason = expiredReasonByLotId[lotId] ?? 'EXPIRED';
      const qty = Number(lot.remainingQtyLbs);

      if (!Number.isFinite(qty) || qty <= 0) return;

      setExpiredSavingByLotId((prev) => ({ ...prev, [lotId]: true }));
      setExpiredError(null);

      try {
        const res = await authorizedFetch(`${API_BASE}/inventory/losses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: lot.productId,
            lotId,
            quantityLbs: qty,
            reason,
          }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const message =
            (body as { message?: string }).message ||
            'Unable to record product loss.';
          throw new Error(message);
        }

        await loadExpiredLots();
      } catch (err) {
        setExpiredError(
          err instanceof Error ? err.message : 'Unable to record product loss.',
        );
      } finally {
        setExpiredSavingByLotId((prev) => ({ ...prev, [lotId]: false }));
      }
    },
    [authorizedFetch, expiredReasonByLotId, loadExpiredLots],
  );

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="workspace-page-title">
              {t('backoffice.expired_products_title', 'Expired Product')}
            </h1>
            <p className="workspace-page-subtitle text-slate-600">
              {t(
                'backoffice.expired_products_subtitle',
                'Expired stock is not shown in the public catalog. Use this page to record it as a loss (lost, damaged, etc.).',
              )}
            </p>
          </div>
        </section>

        <section className="workspace-panel">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">
            {t('backoffice.expired_products_table_title', 'Expired stock')}
          </h2>

          {expiredLoading ? (
            <PaginatedListLoading
              title={t('common.loading', 'Loading…')}
              skeletonRows={4}
            />
          ) : expiredError ? (
            <p className="text-sm text-red-500">{expiredError}</p>
          ) : expiredLots.length === 0 ? (
            <p className="text-sm text-slate-600">{t('backoffice.expired_products_empty', 'No expired lots found.')}</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-left text-xs sm:text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="py-3 px-3">Product</th>
                    <th className="py-3 px-3">Batch</th>
                    <th className="py-3 px-3">Expiry</th>
                    {isAllScope && <th className="py-3 px-3">Department</th>}
                    <th className="py-3 px-3 text-right">Remaining (lb)</th>
                    <th className="py-3 px-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {expiredLots.map((lot) => (
                    <tr key={lot.id}>
                      <td className="py-2 px-3">
                        <div className="font-semibold text-slate-900">{lot.product.name}</div>
                        <div className="text-[11px] text-slate-500">{lot.product.sku}</div>
                      </td>
                      <td className="py-2 px-3 text-slate-700">
                        {lot.batchCode || '—'}
                      </td>
                      <td className="py-2 px-3 text-slate-700">
                        {lot.expiryDate ? new Date(lot.expiryDate).toLocaleDateString() : '—'}
                      </td>
                      {isAllScope && (
                        <td className="py-2 px-3 text-slate-700">
                          {lot.product.department?.slug ? (
                            <button
                              type="button"
                              onClick={() =>
                                switchWorkspaceScope(lot.product.department?.slug || '')
                              }
                              className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
                              title={t(
                                'backoffice.expired_products_switch_department',
                                'Switch scope to this department',
                              )}
                            >
                              {lot.product.department?.name || '—'}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                      <td className="py-2 px-3 text-right text-slate-700">
                        {lot.remainingQtyLbs.toFixed(2)}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            value={expiredReasonByLotId[lot.id] ?? 'EXPIRED'}
                            onChange={(e) =>
                              setExpiredReasonByLotId((prev) => ({
                                ...prev,
                                [lot.id]: e.target.value,
                              }))
                            }
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-[11px] outline-none"
                          >
                            <option value="EXPIRED">Expired</option>
                            <option value="LOST">Lost</option>
                            <option value="DAMAGED">Damaged</option>
                            <option value="THEFT">Theft</option>
                            <option value="OTHER">Other</option>
                          </select>
                          <button
                            type="button"
                            disabled={isAllScope || expiredSavingByLotId[lot.id] === true}
                            onClick={() => void recordExpiredLotLoss(lot)}
                            className="rounded-lg bg-rose-600 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {expiredSavingByLotId[lot.id] ? 'Recording…' : 'Record loss'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

export default ExpiredProductsPage;

